import { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useWebsiteContent } from '../contexts/WebsiteContentContext';
import { ORGANIZATION_SCHEMA } from '../utils/seo';
import { configureGoogleAdsLeadConversion } from '../utils/analytics';

/**
 * Injects tracking scripts (Google Analytics, Facebook Pixel, GTM, custom)
 * and SEO meta tags into the document based on CMS settings.
 */
export default function TrackingScripts() {
  const cms = useWebsiteContent();
  const tracking = cms?.tracking;
  const seo = cms?.seo;

  // Google Analytics (gtag.js) — supports multiple comma-separated IDs.
  // The Google Ads account tag (AW-…) rides on the same gtag loader so
  // conversions can be recorded; the lead-form conversion itself is fired by
  // trackLeadConversion() from each form's success handler.
  useEffect(() => {
    const raw = tracking?.googleAnalyticsIds?.trim();
    if (!raw) return;

    const adsId = tracking?.googleAdsConversionId?.trim();
    const ids = raw.split(',').map(s => s.trim()).filter(Boolean);
    if (adsId && !ids.includes(adsId)) ids.push(adsId);
    if (ids.length === 0) return;

    const primaryId = ids[0];

    // Only add the gtag loader script once
    if (!document.querySelector(`script[src*="googletagmanager.com/gtag/js"]`)) {
      const loader = document.createElement('script');
      loader.src = `https://www.googletagmanager.com/gtag/js?id=${primaryId}`;
      loader.async = true;
      document.head.appendChild(loader);
    }

    const inline = document.createElement('script');
    inline.setAttribute('data-ga-config', 'true');
    inline.textContent = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      ${ids.map(id => `gtag('config', '${id}');`).join('\n      ')}
    `;
    document.head.appendChild(inline);

    return () => { inline.remove(); };
  }, [tracking?.googleAnalyticsIds, tracking?.googleAdsConversionId]);

  // Google Ads "Submit lead form" conversion target — consumed by
  // trackLeadConversion() (website/src/utils/analytics.ts).
  useEffect(() => {
    const adsId = tracking?.googleAdsConversionId?.trim();
    const label = tracking?.googleAdsLeadConversionLabel?.trim();
    configureGoogleAdsLeadConversion(adsId && label ? `${adsId}/${label}` : null);
    return () => configureGoogleAdsLeadConversion(null);
  }, [tracking?.googleAdsConversionId, tracking?.googleAdsLeadConversionLabel]);

  // Facebook Pixel
  useEffect(() => {
    const id = tracking?.facebookPixelId?.trim();
    if (!id || document.querySelector('script[data-fb-pixel]')) return;

    const script = document.createElement('script');
    script.setAttribute('data-fb-pixel', id);
    script.textContent = `
      !function(f,b,e,v,n,t,s)
      {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
      n.callMethod.apply(n,arguments):n.queue.push(arguments)};
      if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
      n.queue=[];t=b.createElement(e);t.async=!0;
      t.src=v;s=b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t,s)}(window, document,'script',
      'https://connect.facebook.net/en_US/fbevents.js');
      fbq('init', '${id}');
      fbq('track', 'PageView');
    `;
    document.head.appendChild(script);

    const noscript = document.createElement('noscript');
    const img = document.createElement('img');
    img.height = 1;
    img.width = 1;
    img.style.display = 'none';
    img.src = `https://www.facebook.com/tr?id=${id}&ev=PageView&noscript=1`;
    noscript.appendChild(img);
    document.body.appendChild(noscript);

    return () => {
      script.remove();
      noscript.remove();
    };
  }, [tracking?.facebookPixelId]);

  // Google Tag Manager
  useEffect(() => {
    const id = tracking?.googleTagManagerId?.trim();
    if (!id || document.querySelector(`script[data-gtm]`)) return;

    const script = document.createElement('script');
    script.setAttribute('data-gtm', id);
    script.textContent = `
      (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
      new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
      j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
      'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
      })(window,document,'script','dataLayer','${id}');
    `;
    document.head.appendChild(script);

    return () => { script.remove(); };
  }, [tracking?.googleTagManagerId]);

  // TrafficGuard — paid traffic monitoring
  // Script must run on every pageview; property ID stored in CMS
  useEffect(() => {
    const id = tracking?.trafficGuardPropertyId?.trim();
    if (!id || document.querySelector('script[data-trafficguard]')) return;

    // Main inline script — sets up dataTrafficGuard and loads tg.js
    const script = document.createElement('script');
    script.setAttribute('data-trafficguard', id);
    script.textContent = `
      var dataTrafficGuard = dataTrafficGuard || [];
      dataTrafficGuard.push(['property', '${id}']);
      dataTrafficGuard.push(['event', 'pageview']);
      (function() {
        var tg = document.createElement('script');
        tg.type = 'text/javascript';
        tg.async = true;
        tg.src = '//tgtag.io/tg.js?pid=${id}';
        var s = document.getElementsByTagName('script')[0];
        s.parentNode.insertBefore(tg, s);
      })();
    `;
    // TrafficGuard recommends placement right after <body> — prepend to body
    document.body.insertBefore(script, document.body.firstChild);

    // noscript fallback pixel
    const noscript = document.createElement('noscript');
    const img = document.createElement('img');
    img.src = `//p.tgtag.io/event?property_id=${id}&event_name=pageview&no_script=1`;
    img.width = 1;
    img.height = 1;
    img.setAttribute('border', '0');
    noscript.appendChild(img);
    document.body.insertBefore(noscript, document.body.firstChild);

    return () => {
      script.remove();
      noscript.remove();
    };
  }, [tracking?.trafficGuardPropertyId]);

  // Custom head scripts
  useEffect(() => {
    const raw = tracking?.customHeadScripts?.trim();
    if (!raw) return;

    const container = document.createElement('div');
    container.innerHTML = raw;

    // <script> elements parsed via innerHTML are flagged "already started"
    // and NEVER execute, even after being moved into <head> (the old Google
    // Ads snippet died this way — see the note above). Rebuild each script
    // node with createElement so pasted tags (verification snippets, pixels)
    // actually run. Other nodes (meta/link/style) are moved as-is.
    const nodes: Node[] = [];
    while (container.firstChild) {
      let node: Node = container.firstChild;
      if (node instanceof HTMLScriptElement) {
        const fresh = document.createElement('script');
        for (const attr of Array.from(node.attributes)) fresh.setAttribute(attr.name, attr.value);
        fresh.text = node.text;
        container.removeChild(node);
        node = fresh;
      }
      // Marker lets the prerender snapshot strip these so they run exactly once
      if (node instanceof Element) node.setAttribute('data-custom-head', 'true');
      document.head.appendChild(node);
      nodes.push(node);
    }

    return () => { nodes.forEach(n => n.parentNode?.removeChild(n)); };
  }, [tracking?.customHeadScripts]);

  // JSON-LD Structured Data (CMS)
  // Existence guard: prerendered pages already carry this tag in the raw HTML.
  useEffect(() => {
    const raw = seo?.jsonLd?.trim();
    if (!raw || document.querySelector('script[data-cms-jsonld]')) return;

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-cms-jsonld', 'true');
    script.textContent = raw;
    document.head.appendChild(script);

    return () => { script.remove(); };
  }, [seo?.jsonLd]);

  // Organization Schema (global)
  // Existence guard: prerendered pages already carry this tag in the raw HTML.
  useEffect(() => {
    if (document.querySelector('script[data-org-schema]')) return;

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-org-schema', 'true');
    script.textContent = JSON.stringify(ORGANIZATION_SCHEMA);
    document.head.appendChild(script);

    return () => { script.remove(); };
  }, []);

  // Per-page titles/descriptions/OG/Twitter tags live in SEOHead — this
  // component must NEVER compete with them. A site-wide CMS siteTitle used to
  // render a second <title>/description here, relying purely on React mount
  // order not to override every page's tags (retired 2026-09-08). Only emit
  // what no page provides:
  const fbAppId = seo?.fbAppId?.trim();

  return (
    <Helmet>
      {fbAppId && <meta property="fb:app_id" content={fbAppId} />}
    </Helmet>
  );
}
